<template>
  <section
    data-cy="contact-overview"
    :data-view-ready="viewReady">
    <contact-search-provider
      ref="contactSearch"
      :query="searchQuery"
      :filters="searchFilters"
      :page="currentPage"
      :items-per-page="Number(nbrPerPage)"
      include-facets
      @results="onResults">
      <template #default="{ hits, facetItems, totalCount }">
        <coozzy-page-title
          v-if="!linkMail"
          :title="linkContact ? $t('message.marketingMessages.titleLinkContact', { contactName: getContactFromLink }) : $t('message.navigation.contact.title')"
          :count="totalCount"
          :count-text="$t('message.generic.contacts')"
          :is-mobile="mobileView"
          :action-text="isEditor ? $t('message.contact.newContact') : '' "
          :clear-filter="isFiltered"
          @click="$bvModal.show('create-contact-modal')"
          @clear-filter="clickOnClear" />
        <coozzy-page-title
          v-else
          :title="$t('message.marketingMessages.linkMailToContact.title')"
          :count="totalCount"
          :count-text="$t('message.generic.contacts')"
          :action-text="$t('message.contact.newContact')"
          :is-mobile="mobileView"
          :clear-filter="isFiltered"
          @click="$bvModal.show('create-contact-modal')"
          @clear-filter="clickOnClear" />
        <!-- Filter -->
        <div class="row justify-content-between align-items-center call-action">
          <div
            v-if="mobileView"
            class="col-10 col-md-2 mb-2 mb-md-0">
            <coozzy-button
              class="w-100 my-2"
              @click="showFilters = !showFilters">
              <coozzy-filter-init-icon />
              {{ showFilters ? $t('message.generic.hideFilters') : $t('message.generic.showFilters') }}
            </coozzy-button>
          </div>
          <div
            v-if="mobileView"
            class="col-2 col-md-2 mb-2 text-right pl-0">
            <coozzy-button
              class="w-100 my-2"
              @click="clickOnClear">
              <coozzy-filter-icon />
            </coozzy-button>
          </div>
          <div class="col-12 col-md-2 mb-2 mb-md-0">
            <debounced-search-box
              ref="searchBox"
              data-cy="contact-overview-search-input"
              :default-value="searchQuery"
              :delay="300"
              @search="onSearchChanged" />
          </div>
          <template v-if="!mobileView || (mobileView && showFilters)">
            <div class="col-12 col-md-2 mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="filterRole"
                data-cy="contact-overview-roles-filter"
                :options="sortedFilterValues(filterRoleOption(facetItems.types), customLabel)"
                :multiple="true"
                :placeholder="$t('message.generic.roles')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabel"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="clearCheckbox()"
                @remove="clearCheckbox()" />
            </div>
            <div class="col-12 col-md-2 mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="filterIndustries"
                data-cy="contact-overview-industry-filter"
                :options="sortedFilterValues(getFilterIndustriesOptions(facetItems.industries), customLabelIndustries)"
                :multiple="true"
                :placeholder="$t('message.generic.industry')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabelIndustries"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="clearCheckbox()"
                @remove="clearCheckbox()" />
            </div>
            <div class="col-12 col-md-2 mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="filterTags"
                data-cy="contact-overview-tags-filter"
                :options="sortedFilterValues(getFilterTagsOptions(facetItems.tags), customLabelTags)"
                :multiple="true"
                :placeholder="$t('message.generic.tags')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabelTags"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="clearCheckbox()"
                @remove="clearCheckbox()" />
            </div>
            <div
              class="col-12 col-md-2 mb-2 mb-md-0">
              <coozzy-form-select
                v-model="filterPreferredCommunicationChannel"
                data-cy="contact-overview-pref-communication-filter">
                <option
                  :value="null">
                  {{ $t('message.contact.preferredCommunicationChannel') }}
                </option>
                <option value="PREFERRED_COMMUNICATION_CHANNEL_UNDEFINED">
                  -
                </option>
                <option value="PREFERRED_COMMUNICATION_CHANNEL_EMAIL">
                  {{ $t('message.contact.preferredCommunicationChannelType.email') }}
                </option>
                <option value="PREFERRED_COMMUNICATION_CHANNEL_LETTER">
                  {{ $t('message.contact.preferredCommunicationChannelType.letter') }}
                </option>
              </coozzy-form-select>
            </div>
            <div class="col-12 col-md-2 mb-2 mb-md-0">
              <coozzy-form-select
                v-model="filterStatus"
                data-cy="contact-overview-status-filter">
                <option value="all">
                  {{ $t('message.generic.all') }}
                </option>
                <option value="active">
                  {{ $t('message.generic.active') }}
                </option>
                <option value="inactive">
                  {{ $t('message.generic.inactive') }}
                </option>
              </coozzy-form-select>
            </div>
            <div
              v-if="$route.name === 'AdminContactOverview' || $route.name === 'AssetContactOverview' || $route.name === 'MarketingContactOverview' || $route.name === 'AccountingContactOverview'"
              class="col-12 col-md-2 mt-2">
              <coozzy-multiselect
                v-model="filterOwner"
                data-cy="contact-overview-owner-filter"
                :options="ownerFacetOptions(facetItems.ownerNames)"
                :multiple="true"
                :placeholder="$t('message.tableColumnHeadings.owner')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customOwnerLabel"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @search-change="ownerFacetQuery = $event"
                @select="clearCheckbox()"
                @remove="clearCheckbox()" />
            </div>
            <div
              class="col-12 col-md-2 mb-2 mb-md-0 mt-2"
              :class="$route.name === 'OwnerContactOverview' ? ' offset-md-10' :' offset-md-8'">
              <coozzy-form-checkbox
                :id="'isOnlyInterestedParty'"
                ref="isOnlyInterestedParty"
                v-model="filterShowInterestedParties"
                data-cy="contact-overview-interested-parties-checkbox"
                class="label-padding-top-005rem"
                :initial="filterShowInterestedParties"
                @change="onInterestedPartiesChanged">
                {{
                  $t('message.generic.showInterestedParties')
                }}
              </coozzy-form-checkbox>
            </div>
          </template>
        </div>
        <div
          id="rowConfig"
          class="row mt-2 align-items-center">
          <div
            v-if="!linkMail && !linkContact"
            class="col-8 col-md-2">
            <coozzy-dropdown
              design="green"
              size="sm"
              class="w-100"
              data-cy="contact-selected-entries-button"
              :text="selectedEntriesText">
              <coozzy-dropdown-item
                v-if="isTicketingEnabled && (isEditor || isTicketEditor || isTicketCreator)"
                data-cy="contact-selected-create-ticket-item"
                :disabled="selectedEntries.length === 0"
                @click="createTicket">
                {{ $t('message.ticketOverview.createTicket') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-item
                v-if="isEditor"
                data-cy="contact-selected-create-activity-item"
                :disabled="selectedEntries.length === 0"
                @click="showActivity()">
                {{ $t('message.contact.activity.create') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-item
                data-cy="contact-selected-create-reminder-item"
                :disabled="selectedEntries.length === 0"
                @click="$refs['reminder-creation'].show()">
                {{ $t('message.calendar.reminder.create') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-item
                v-if="isEditor"
                data-cy="contact-selected-send-email-item"
                :disabled="selectedEntries.length === 0"
                @click="serialEmail()">
                {{ selectedEntries.length === 1 ? $t('message.generic.oneEmail') : $t('message.generic.serialEmail') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-item
                v-if="isEditor"
                data-cy="contact-selected-send-letter-item"
                :disabled="selectedEntries.length === 0"
                @click="serialLetter()">
                {{ selectedEntries.length === 1 ? $t('message.generic.oneSerialLetter') : $t('message.generic.serialLetter') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-space-divider v-if="isEditor" />
              <coozzy-dropdown-item
                v-if="isEditor"
                data-cy="contact-selected-edit-address-item"
                :disabled="selectedEntries.length === 0"
                @click="editAddress()">
                {{ $t('message.contact.actions.editAddress') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-item
                v-if="isEditor"
                data-cy="contact-selected-duplicate-item"
                :disabled="selectedEntries.length === 0"
                @click="openDuplicateContactsModal()">
                {{ $t('message.contact.actions.duplicateToOtherOwnerbase') }}
              </coozzy-dropdown-item>
              <coozzy-dropdown-space-divider v-if="isEditor" />
              <div
                v-if="isEditor && filterStatus === 'active'"
                class="row m-0"
                :class="selectedEntries.length === 0 || checkIfOneIsTenant ? 'coozzyDropdownItemTooltip' : ''">
                <coozzy-dropdown-item
                  class="col pr-0 pl-0"
                  data-cy="contact-selected-deactivate-item"
                  :disabled="selectedEntries.length === 0 || checkIfOneIsTenant"
                  @click="selectedEntries.length !== 0 ? openConfirmationModal() : ''">
                  {{ $t('message.generic.inactivate') }}
                </coozzy-dropdown-item>
                <div class="pl-0">
                  <coozzy-info-circle-icon
                    v-if="checkIfOneIsTenant"
                    v-b-tooltip.hover.html="$t('message.deleteErrors.inactivatecontactisActiveTenancyTooltip')"
                    class="mt-2" />
                </div>
              </div>
              <coozzy-dropdown-item
                v-else-if="isEditor && filterStatus === 'inactive'"
                data-cy="contact-selected-activate-item"
                :disabled="selectedEntries.length === 0"
                @click="selectedEntries.length !== 0 ? openConfirmationModal() : ''">
                {{ $t('message.generic.activate') }}
              </coozzy-dropdown-item>
            </coozzy-dropdown>
          </div>
          <div class="col-md-2 col-12 order-3 order-md-1">
            <template v-if="selectAllVisibleEntries && nbrTotalOfHits > nbrPerPage && nbrTotalOfHits <= 1000">
              <coozzy-form-checkbox
                v-model="selectAllEntries"
                data-cy="contact-overview-select-all-entries-checkbox"
                :initial="selectAllEntries"
                :class="'p-0 w-100 btn-height-auto'"
                name="selectAllEntries"
                button>
                {{ selectAllEntries ? $t('message.generic.unselectHiddenEntries', { amount: nbrTotalOfHits }) : $t('message.generic.selectHiddenEntries', { amount: nbrTotalOfHits }) }}
              </coozzy-form-checkbox>
            </template>
            <a
              v-if="isFiltered"
              ref="clearClick"
              @click="initFilter()" />
          </div>
          <div
            v-if="isCompanyAdmin === true && !mobileView"
            :class="[linkMail === '' ? 'offset-md-5' : 'offset-md-7', !linkContact ? 'offset-md-5' : 'offset-md-7']"
            class="col-md-1 d-flex justify-content-end align-items-center order-md-2">
            <div
              data-cy="contact-overview-assemble-list-button"
              @click="openAssembleListModal()">
              <coozzy-filters-icon
                v-b-tooltip="{container: '#rowConfig', title: $t('message.generic.tooltipAssembleList')}" />
            </div>
          </div>
          <div
            class="col-md-2 col-4 mt-md-2 mt-0 pl-md-2 pl-0 order-md-3"
            :class="[!isCompanyAdmin ? 'offset-md-6' : '']">
            <coozzy-form-select
              v-model="nbrPerPage"
              data-cy="contact-overview-entries-select"
              name="numberEntries"
              class="select-entries float-right">
              <option value="10">
                10 {{ $t('message.generic.entries') }}
              </option>
              <option value="50">
                50 {{ $t('message.generic.entries') }}
              </option>
              <option value="100">
                100 {{ $t('message.generic.entries') }}
              </option>
              <option value="500">
                500 {{ $t('message.generic.entries') }}
              </option>
              <option value="1000">
                1000 {{ $t('message.generic.entries') }}
              </option>
            </coozzy-form-select>
          </div>
        </div>
        <b-table
          ref="contactOverviewTable"
          bordered
          hover
          responsive="true"
          stacked="md"
          selectable
          select-mode="single"
          class="mt-2"
          :fields="fields"
          :items="filteredHits(hits)"
          @row-clicked="onRowClicked"
          @row-middle-clicked="onMiddlelicked">
          <!-- Busy state -->
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <coozzy-spinner />
            </div>
          </template>

          <!-- Headings -->
          <template
            v-if="!linkContact"
            #head(checkbox)>
            <coozzy-form-checkbox
              ref="header_checkbox"
              :initial="selectAllVisibleEntries"
              data-cy="contact-select-all-checkbox"
              @change="(value) => { handleCheckboxesInsideTable(value) }" />
          </template>
          <template #head(addressNumber)>
            {{ $t('message.tableColumnHeadings.addressNumber') }}
          </template>
          <template #head(contactName)>
            {{ $t('message.tableColumnHeadings.contactName') }}
          </template>
          <template #head(contactPersons)>
            {{ $t('message.tableColumnHeadings.contactPersons') }}
          </template>
          <template #head(address)>
            {{ $t('message.tableColumnHeadings.address') }}
          </template>
          <template #head(communication)>
            {{ $t('message.tableColumnHeadings.communication') }}
          </template>
          <template #head(roles)>
            {{ $t('message.tableColumnHeadings.roles') }}
          </template>
          <template #head(owner)>
            {{ $t('message.tableColumnHeadings.owner') }}
          </template>
          <template #head(tags)>
            {{ $t('message.tableColumnHeadings.tags') }}
          </template>
          <template #head(note)>
            {{ $t('message.tableColumnHeadings.note') }}
          </template>

          <!-- Columns -->
          <template
            v-if="!linkContact"
            #cell(checkbox)="data">
            <coozzy-form-checkbox
              :id="'check_' + data.item.objectID"
              :initial="isContactSelected(data.item)"
              data-cy="contact-row-checkbox"
              @change="(value) => { contactSelected(data.item, value) }" />
          </template>
          <template #cell(addressNumber)="data">
            <span data-cy="contact-row-address-id">{{ data.item.numericId }}</span>
          </template>
          <template
            #cell(contactName)="data">
            <span data-cy="contact-row-name">{{ data.item.name }} <span class="text-red">{{ data.item.active ? '' : '(' + $t('message.generic.inactive') + ')' }}</span></span>
          </template>
          <!-- Contact person -->
          <template
            #cell(contactPersons)="data">
            <template v-if="data.item.contactPersons && data.item.contactPersons.length > 0">
              <span
                v-for="(person, index) in data.item.contactPersons"
                :key="person.id + index"
                data-cy="contact-row-contact-persons">
                <span v-if="person.active">{{ person.name }}<br></span>
              </span>
            </template>
            <template v-else>
              <span data-cy="contact-row-contact-persons">-</span>
            </template>
          </template>
          <!-- Address -->
          <template
            #cell(address)="data">
            <span data-cy="contact-row-address">
              <address-text :address="data.item.address" />
            </span>
          </template>
          <!-- Communication -->
          <template
            #cell(communication)="data">
            <div data-cy="contact-row-communication">
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'mobile') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-mobile-alt-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'mobile')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'mobile') }}
                  </a>
                </div>
              </div>
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'home') !== '-' && !data.item.isCompany"
                class="row">
                <div class="col-1">
                  <coozzy-phone-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'home')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'home') }}
                  </a>
                </div>
              </div>
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'work') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-headset-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'work')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'work') }}
                  </a>
                </div>
              </div>
              <div
                v-if="data.item.email !== ''"
                class="row">
                <div class="col-1">
                  <img
                    class="email-icon-img"
                    src="@/assets/icon/arroba.svg"
                    alt="Placeholder">
                </div>
                <div class="col">
                  <template v-if="data.item.email !== ''">
                    <a
                      class="link"
                      tabindex="0"
                      @click="sendEmailModal(data.item)">
                      {{ data.item.email }}
                    </a>
                  </template>
                  <template v-else>
                    {{ data.item.email | displayOptionalValue }}
                  </template>
                </div>
              </div>
              <div
                v-if="data.item.website !== ''"
                class="row">
                <div class="col-1">
                  <coozzy-globe-icon />
                </div>
                <div class="col">
                  <template v-if="data.item.website !== ''">
                    <a
                      class="link"
                      tabindex="0"
                      target="_blank"
                      :href="getUrlWebsite(data.item.website)">
                      {{ data.item.website }}
                    </a>
                  </template>
                  <template v-else>
                    {{ data.item.website | displayOptionalValue }}
                  </template>
                </div>
              </div>
            </div>
          </template>
          <!-- Roles -->
          <template
            #cell(roles)="data">
            <span data-cy="contact-row-roles">
              {{ getListRole(data.item) }}
              <template v-if="getTranslatedIndustries(data.item) !== '-'">
                <br>
                {{ $t('message.generic.industry') }}:<br>
                {{ getTranslatedIndustries(data.item) }}
              </template>
            </span>
          </template>
          <!-- Owner -->
          <template
            #cell(owner)="data">
            <span data-cy="contact-row-owner">{{ data.item.owner.name }}</span>
          </template>
          <!-- note -->
          <template
            #cell(tags)="data">
            {{ data.item.tags.length > 0 ? data.item.tags.join(', ') : '-' }}
          </template>
          <!-- Owner -->
          <template
            #cell(note)="data">
            {{ data.item.note }}
          </template>
        </b-table>
        <pagination-rest
          ref="paginator"
          :current-page="currentPage"
          :total-pages="totalPageCount"
          @changed-page="onPageChanged" />
      </template>
    </contact-search-provider>

    <b-modal
      :id="'confirmationModal'"
      ref="confirmationModal"
      data-cy="contact-bulk-status-confirmation-modal"
      no-close-on-backdrop
      hide-footer
      :title="filterStatus === 'inactive' ? $t('message.employeesSettings.activateContactConfirmation') : $t('message.employeesSettings.deactivateContactConfirmation')">
      <div class="col p-0">
        <p v-if="filterStatus === 'inactive'">
          {{ $t('message.employeesSettings.activeUserText') }}
        </p>
        <p v-else>
          {{ $t('message.employeesSettings.deactivateContactText') }}
        </p>
      </div>
      <div class="col p-0">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          :disabled="disabledDeleteBtn"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          v-if="filterStatus === 'inactive'"
          data-cy="contact-bulk-activate-confirm-button"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="activateContact()">
          {{ $t('message.generic.activate') }}
        </coozzy-button>
        <coozzy-button
          v-else
          data-cy="contact-bulk-deactivate-confirm-button"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="deactivateContact()">
          {{ $t('message.generic.inactivate') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      :id="'duplicate-contact-modal'"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.contact.duplicateToOtherOwnerbase.title')">
      <div class="col pl-0 pr-0">
        <label
          for="duplicateOwnerId"
          class="mb-0">
          {{ $t('message.contact.duplicateToOtherOwnerbase.selectOwner') }}
        </label>
        <coozzy-form-select
          id="duplicateOwnerId"
          v-model="selectedDuplicateOwnerId">
          <option :value="null">
            -
          </option>
          <option
            v-for="owner in duplicateOwnerAccounts"
            :key="owner.id"
            :value="owner.id">
            {{ owner.name }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col pl-0 pr-0 mt-3">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          :disabled="duplicatingContacts"
          @click="closeDuplicateContactsModal()">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="!selectedDuplicateOwnerId || duplicatingContacts"
          @click="duplicateContactsToOwnerbase()">
          {{ $t('message.contact.duplicateToOtherOwnerbase.submit') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      :id="'link-mail-to-contact'"
      :ref="'link-mail-to-contact'"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingMessages.linkMailToContact.modalTitle')">
      <div class="col">
        <p>{{ $t('message.marketingMessages.linkMailToContact.modalText') }}</p>
      </div>
      <div class="col">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          @click="$bvModal.hide('link-mail-to-contact')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          @click="linkMailToContact()">
          {{ $t('message.generic.form.save') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      :id="'confirmationModalLinkContact'"
      ref="confirmationModalLinkContact"
      data-cy="contact-link-confirmation-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.contact.linkContact.title')">
      <div class="col pl-0">
        <p>{{ $t('message.contact.linkContact.text') }}</p>
      </div>
      <div class="col pl-0">
        <coozzy-button
          data-cy="contact-link-cancel-button"
          size="small"
          class="mb-0 border"
          design="transparent"
          @click="$bvModal.hide('confirmationModalLinkContact')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="contact-link-confirm-button"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledLinkBtn"
          @click="linkContacts()">
          {{ $t('message.generic.confirm') }}
        </coozzy-button>
      </div>
    </b-modal>
    <create-contact-modal
      :for-owner-id="isOwnerModule ? accountId : null"
      :show-add-another="!linkContact"
      :show-search="false"
      :show-request-sources="false"
      :set-interested-party-role="false"
      :enable-delete="false"
      :restrict-new-contact-type-to="linkContactType === 'person' ? 'private' : (linkContactType === 'company' ? 'company' : null)"
      :initial-email="email && email.from ? email.from.email : ''"
      @contact-created="contactChanged"
      @contact-updated="contactChanged"
      @contact-deleted="contactChanged" />
    <reminder-creation-modal
      ref="reminder-creation"
      :references-ids="selectedEntriesIds"
      :contact-id="selectedEntriesIds.length === 1 ? selectedEntriesIds[0] : null"
      :source="'addressBook'"
      @new-reminder="clearCheckbox" />
    <create-activity-modal
      ref="activity-creation"
      :key="keyActivity"
      :ids="selectedEntriesIds"
      @activity-created="clearCheckbox" />
    <add-address-modal
      :list-contacts="selectAllEntries ? allEntries : selectedEntries"
      @address-added="addressAdded" />
    <assemble-list-modal
      ref="assembleListModal"
      modal-id="assembleListModal"
      :fields="selectedFields"
      :default-fields="dataFields"
      @save-fields="saveFields" />
  </section>
</template>

<script>
import ContactApi from '../../misc/apis/ContactApi'
import { executeBulkInChunks } from '../../misc/utils/bulkOperations'
import AddressText from '../../framework/components/misc/AddressText'
import Vue from 'vue'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
import CoozzyDropdown from '../../framework/components/dropdown/CoozzyDropdown'
import CoozzyMultiselect from '../../framework/components/multiselect/CoozzyMultiselect'
import CoozzyDropdownItem from '../../framework/components/dropdown/CoozzyDropdownItem'
import CoozzyFormSelect from '../../framework/components/form/select/CoozzyFormSelect'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner'
import CoozzyFormCheckbox from '../../framework/components/form/checkbox/CoozzyFormCheckbox'
import CoozzyPhoneIcon from '../../framework/components/icons/CoozzyPhoneIcon'
import CoozzyHeadsetIcon from '../../framework/components/icons/CoozzyHeadsetIcon'
import CoozzyGlobeIcon from '../../framework/components/icons/CoozzyGlobeIcon'
import CoozzyPageTitle from '../../framework/layout/CoozzyPageTitle'
import CoozzyDropdownSpaceDivider from '../../framework/components/dropdown/CoozzyDropdownSpaceDivider'
import ReminderCreationModal from '@/calendar/components/ReminderCreationModal'
import CreateActivityModal from '@/contact/components/CreateActivityModal'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import DebouncedSearchBox from '@/framework/components/search/DebouncedSearchBox.vue'
import PaginationRest from '@/framework/components/search/PaginationRest.vue'
import { awaitSearchIndexUpdate } from '@/misc/utils/searchIndexing'
import MessageApi from '../../misc/apis/MessageApi'
import { mapMutations } from 'vuex'
import { tables } from '@/mixins/tables'
import { contact } from '@/mixins/contact'
import { routeChecks } from '@/mixins/routeChecks'
import AddAddressModal from '@/contact/components/AddAddressModal'
import CreateContactModal from '@/contact/components/CreateContactModal'
import { user } from '@/mixins/user'
import CoozzyMobileAltIcon from '@/framework/components/icons/CoozzyMobileAltIcon'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon'
import { v4 as uuidv4 } from 'uuid'
import { mail, eventBus, isMailTypePrepCurrent } from '@/mixins/mail'
import CoozzyFiltersIcon from '@/framework/components/icons/CoozzyFiltersIcon.vue'
import CoozzyFilterInitIcon from '@/framework/components/icons/CoozzyFilterInitIcon.vue'
import AssembleListModal from '@/contact/components/AssembleListModal.vue'
import SearchApi from '@/misc/apis/SearchApi'
import { responsivity } from '@/mixins/responsivity'
import ShareApi from '@/misc/apis/ShareApi'
import UserApi from '@/misc/apis/UserApi'

export default {
  name: 'ContactOverview',
  components: {
    AssembleListModal,
    CoozzyFilterInitIcon,
    CoozzyFiltersIcon,
    CoozzyInfoCircleIcon,
    CoozzyMobileAltIcon,
    CreateContactModal,
    ContactSearchProvider,
    DebouncedSearchBox,
    ReminderCreationModal,
    CreateActivityModal,
    CoozzyDropdownSpaceDivider,
    CoozzyPageTitle,
    PaginationRest,
    CoozzyGlobeIcon,
    CoozzyPhoneIcon,
    CoozzyFormCheckbox,
    CoozzySpinner,
    CoozzyFormSelect,
    CoozzyDropdownItem,
    CoozzyMultiselect,
    CoozzyDropdown,
    CoozzyButton,
    CoozzyHeadsetIcon,
    AddAddressModal,
    AddressText
  },
  mixins: [routeChecks, tables, contact, user, mail, responsivity],
  props: {
    email: {
      type: String,
      default: ''
    },
    linkMail: {
      type: String,
      default: ''
    },
    linkContact: {
      type: String,
      default: null
    },
    linkContactType: {
      type: String,
      default: null
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.contact.title')
    }
  },
  data() {
    return {
      showFilters: false,
      action: '',
      keyActivity: 0,
      viewReady: false,
      selectedEntries: [],
      pageHits: [],
      selectedLinkContact: null,
      searchQuery: '',
      ownerFacetQuery: '',
      nbrPerPage: 10,
      currentPage: 1,
      disabledDeleteBtn: false,
      disabledLinkBtn: false,
      preferredCommunicationChannelOptions: [{ label: 'PREFERRED_COMMUNICATION_CHANNEL_EMAIL', value: 'PREFERRED_COMMUNICATION_CHANNEL_EMAIL' }, { label: 'PREFERRED_COMMUNICATION_CHANNEL_LETTER', value: 'PREFERRED_COMMUNICATION_CHANNEL_LETTER' }],
      fields: [],
      dataFields: [
        {
          key: 'addressNumber',
          label: this.$t('message.tableColumnHeadings.addressNumber'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'contactName',
          label: this.$t('message.tableColumnHeadings.contactName'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'contactPersons',
          label: this.$t('message.tableColumnHeadings.contactPersons'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'address',
          label: this.$t('message.tableColumnHeadings.address'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'communication',
          label: this.$t('message.tableColumnHeadings.communication'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'roles',
          label: this.$t('message.tableColumnHeadings.roles'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: true
        },
        {
          key: 'owner',
          label: this.$t('message.tableColumnHeadings.owner'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true,
          visible: false
        },
        {
          key: 'tags',
          label: this.$t('message.tableColumnHeadings.tags'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false,
          visible: true
        },
        {
          key: 'note',
          label: this.$t('message.tableColumnHeadings.note'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false,
          visible: true
        }
      ],
      selectedFields: [],
      contactDetail: {},
      filterRole: [],
      filterPreferredCommunicationChannel: null,
      filterIndustries: [],
      filterTags: [],
      contactPersonsIds: [],
      selectedRow: null,
      filterOwner: [],
      filterStatus: 'active',
      filterShowInterestedParties: false,
      duplicateOwnerAccounts: [],
      selectedDuplicateOwnerId: null,
      duplicatingContacts: false,
      duplicatedContactsBuffer: [],
      duplicatedContactsBufferStorageTtlMs: 5 * 60 * 1000,
      linkContactResolved: '',
      nbrTotalOfHits: 0,
      totalPageCount: 0,
      selectAllVisibleEntries: false,
      selectAllEntries: false,
      allEntries: [],
      modalHiddenHandler: null,
      /** Facet values read from the URL, hydrated into full items once the first facets arrive. */
      pendingFacetSelections: null,
      /** Skip resetting page while restoring filters from the URL (including facet hydration). */
      suppressFilterPageReset: false
    }
  },
  computed: {
    checkIfOneIsTenant() {
      const listEntries = this.selectAllEntries ? this.allEntries : this.selectedEntries
      return listEntries.filter(x => x && !x.canBeSetInactive).length > 0
    },
    selectedEntriesIds() {
      const listEntries = this.selectAllEntries ? this.allEntries : this.selectedEntries
      return listEntries.filter(x => x !== undefined && x !== null).map(obj => obj.objectID)
    },
    selectedEntryIdsSet() {
      return new Set(this.selectedEntries.map(entry => this.contactId(entry)).filter(Boolean))
    },
    searchFilters() {
      const filters = { isOwner: false }
      if (!(this.linkContact && this.linkContactType !== '' && this.linkContactType)) {
        filters.isEmployee = false
      }
      if (!this.filterShowInterestedParties) {
        filters.isOnlyInterestedParty = false
      }
      if (this.filterStatus === 'active') {
        filters.active = true
      } else if (this.filterStatus === 'inactive') {
        filters.active = false
      }
      if (this.filterPreferredCommunicationChannel) {
        filters.preferredCommunicationChannels = [this.filterPreferredCommunicationChannel]
      }
      if (this.filterRole.length > 0) {
        filters.types = this.filterRole.map(x => x.value)
      }
      if (this.filterIndustries.length > 0) {
        filters.industries = this.filterIndustries.map(x => x.value)
      }
      if (this.filterTags.length > 0) {
        filters.tags = this.filterTags.map(x => x.value)
      }
      if (this.filterOwner.length > 0) {
        filters.ownerNames = this.filterOwner.map(x => x.value)
      }
      return filters
    },
    routeQueryState() {
      const query = {}
      if (this.searchQuery) {
        query.query = this.searchQuery
      }
      if (this.currentPage > 1) {
        query.page = String(this.currentPage)
      }
      if (this.filterRole.length > 0) {
        query.types = this.filterRole.map(x => x.value).join(',')
      }
      if (this.filterIndustries.length > 0) {
        query.industries = this.filterIndustries.map(x => x.value).join(',')
      }
      if (this.filterTags.length > 0) {
        query.tags = this.filterTags.map(x => x.value).join(',')
      }
      if (this.filterOwner.length > 0) {
        query.owner = this.filterOwner.map(x => x.value).join(',')
      }
      if (this.filterStatus !== 'active') {
        query.status = this.filterStatus
      }
      if (this.filterPreferredCommunicationChannel) {
        query.channel = this.filterPreferredCommunicationChannel
      }
      if (this.filterShowInterestedParties) {
        query.interestedParties = 'true'
      }
      return query
    },
    selectedEntriesText() {
      const count = this.selectAllEntries && this.selectAllVisibleEntries ? this.nbrTotalOfHits : this.selectedEntries.length
      return this.$t('message.generic.selectedEntries') + ':  ' + count
    },
    getContactFromLink() {
      return this.linkContactResolved !== '' ? this.linkContactResolved : ''
    },
    isFiltered() {
      return (this.filterPreferredCommunicationChannel !== null && this.filterPreferredCommunicationChannel !== '') ||
        this.filterStatus !== 'all' ||
        this.filterShowInterestedParties ||
        this.searchQuery !== '' ||
        this.filterRole.length > 0 ||
        this.filterIndustries.length > 0 ||
        this.filterTags.length > 0 ||
        this.filterOwner.length > 0
    }
  },
  watch: {
    mobileView: function (newVal) {
      if (newVal) {
        this.selectedFields = this.selectedFields.filter(x => ['checkbox', 'contactName', 'address', 'communication'].includes(x.key))
      } else {
        this.selectedFields = JSON.parse(JSON.stringify(this.dataFields))
      }
    },
    filterPreferredCommunicationChannel: function (newVal) {
      if (newVal) {
        this.clearCheckbox()
      }
    },
    filterStatus: function () {
      this.handleCheckboxesInsideTable(false)
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
    },
    nbrPerPage: function (newVal) {
      localStorage.setItem('contact-overview-page-size-' + this.$route.name, newVal)
      if (!this.suppressFilterPageReset) {
        this.currentPage = 1
      }
      this.clearCheckbox()
      this.handleCheckboxesInsideTable(false)
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
    },
    currentPage: function () {
      this.handleCheckboxesInsideTable(false)
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
    },
    searchFilters: function () {
      if (this.suppressFilterPageReset) {
        return
      }
      this.currentPage = 1
    },
    routeQueryState: {
      deep: true,
      handler: function () {
        this.syncRouteQuery()
      }
    },
    linkContact: function () {
      if (!this.linkContact && !this.fields.some(obj => obj.key === 'checkbox')) {
        this.fields.unshift(
          {
            key: 'checkbox',
            label: '',
            thClass: 'th-checkbox',
            tdClass: 'align-middle border-right-0 checkbox-cell'
          })
      }
    },
    selectAllEntries: async function (newValue) {
      if (newValue) {
        this.handleCheckboxesInsideTable(newValue)
        // Skip refetching when the previous fetch already covered the current result set
        if (this.nbrTotalOfHits !== this.allEntries.length) {
          this.allEntries = await this.$refs.contactSearch.fetchAll()
        }
      }
    }
  },
  mounted: function () {
    this.restoreFilterStateFromRoute()
    this.loadData()
    this.restoreDuplicatedContactsBuffer()
    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-send-mail') {
        this.clearCheckbox()
      }
    }
    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
  },
  methods: {
    ...mapMutations('message', [
      'setSendMailContacts',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply',
      'setSendMailSecondaryTitle',
      'setUseBcc',
      'setSendMailContactsWithoutEmail',
      'setSelectedEntries'
    ]),
    /**
     * Restores query, page and filter selections from the URL. Facet selections only carry
     * their values here; they are turned into full multiselect items once facets arrive.
     */
    restoreFilterStateFromRoute() {
      const query = this.$route.query || {}
      const toValues = value => (value ? String(value).split(',').filter(x => x !== '') : [])
      this.suppressFilterPageReset = true
      try {
        this.searchQuery = query.query ? String(query.query) : ''
        const page = parseInt(query.page)
        this.currentPage = !isNaN(page) && page > 0 ? page : 1
        if (query.status) {
          this.filterStatus = String(query.status)
        }
        if (query.channel) {
          this.filterPreferredCommunicationChannel = String(query.channel)
        }
        this.filterShowInterestedParties = query.interestedParties === 'true'
        this.pendingFacetSelections = {
          filterRole: toValues(query.types),
          filterIndustries: toValues(query.industries),
          filterTags: toValues(query.tags),
          filterOwner: toValues(query.owner)
        }
        const storedPageSize = localStorage.getItem('contact-overview-page-size-' + this.$route.name)
        if (storedPageSize) {
          this.nbrPerPage = parseInt(storedPageSize)
        }
      } finally {
        this.suppressFilterPageReset = false
      }
    },
    hydrateFacetSelections(facets) {
      if (!this.pendingFacetSelections) {
        return
      }
      const pending = this.pendingFacetSelections
      this.pendingFacetSelections = null
      const hydrate = (values, buckets) => values.map(value => {
        const bucket = (buckets || []).find(x => x.value === value)
        return { label: value, value: value, count: bucket ? bucket.count : 0, isRefined: true }
      })
      this.suppressFilterPageReset = true
      try {
        this.filterRole = hydrate(pending.filterRole, facets.types)
        this.filterIndustries = hydrate(pending.filterIndustries, facets.industries)
        this.filterTags = hydrate(pending.filterTags, facets.tags)
        this.filterOwner = hydrate(pending.filterOwner, facets.ownerNames)
      } finally {
        this.suppressFilterPageReset = false
      }
    },
    syncRouteQuery() {
      const target = this.routeQueryState
      const preserved = {}
      Object.entries(this.$route.query || {}).forEach(([key, value]) => {
        if (!['query', 'page', 'types', 'industries', 'tags', 'owner', 'status', 'channel', 'interestedParties'].includes(key)) {
          preserved[key] = value
        }
      })
      const next = { ...preserved, ...target }
      if (JSON.stringify(next) === JSON.stringify(this.$route.query)) {
        return
      }
      this.$router.replace({ query: next }).catch(() => {})
    },
    onResults(response) {
      this.pageHits = response.hits || []
      this.nbrTotalOfHits = response.totalCount
      this.totalPageCount = response.totalPageCount
      this.viewReady = true
      this.hydrateFacetSelections(response.facets)
    },
    onSearchChanged(value) {
      this.searchQuery = value
      this.currentPage = 1
      this.clearCheckbox()
    },
    onPageChanged(page) {
      this.currentPage = page
      this.clearCheckbox()
    },
    onInterestedPartiesChanged() {
      if (!this.filterShowInterestedParties && this.filterRole.find(x => x.value === 'INTERESTED_PARTY')) {
        this.filterRole = this.filterRole.filter(x => x.value !== 'INTERESTED_PARTY')
      }
      this.clearCheckbox()
    },
    ownerFacetOptions(items) {
      if (!this.ownerFacetQuery) {
        return items
      }
      const needle = this.ownerFacetQuery.toLowerCase()
      return items.filter(item => String(item.value).toLowerCase().includes(needle))
    },
    async loadData() {
      if (!this.linkContact && !this.linkMail) {
        this.fields.unshift(
          {
            key: 'checkbox',
            label: '',
            thClass: 'th-checkbox',
            tdClass: 'align-middle border-right-0 checkbox-cell',
            selected: true,
            visible: true
          })
      }
      if (['AdminContactOverview', 'MarketingContactOverview', 'AssetContactOverview', 'AccountingContactOverview'].includes(this.$route.name)) {
        this.dataFields.find(x => x.key === 'owner').visible = true
      }
      if (this.linkMail) {
        MessageApi.listMailsByIds([this.linkMail])
          .then(response => {
            this.email = response.mails[0]
          })
      }
      if (this.linkContact) {
        ContactApi.contactResolve([this.linkContact])
          .then(response => {
            this.linkContactResolved = response.persons.concat(response.companies).find(x => x.id === this.linkContact).name
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          })
      }
      this.dataFields = this.dataFields.filter(x => x.selected && x.visible).concat(this.dataFields.filter(x => !x.selected))
      SearchApi.loadSettings(this.accountId).then(response => {
        const settings = response.tableSettings
        let hasOldList = false
        if (settings.length > 0) {
          settings.forEach(setting => {
            if (setting.columns.length > 0 && setting.table === 'contactOverview') {
              hasOldList = true
              this.loadFields(setting)
            }
          })
        }
        if (hasOldList === false) {
          this.fields.push(...this.dataFields.filter(x => x.selected))
          this.selectedFields = JSON.parse(JSON.stringify(this.dataFields))
        }
      }).catch(e => {
        console.log(e)
      })
      setTimeout(() => {
        this.$refs.searchBox?.setFocusInput()
      }, 500)
    },
    normalizeContactTypes(types) {
      if (!types || !Array.isArray(types)) {
        return []
      }
      return types.map(t => String(t).toLowerCase())
    },
    computeIsOnlyInterestedParty(types) {
      const normalized = this.normalizeContactTypes(types).filter(t => t && t !== 'none')
      return normalized.length === 1 && normalized[0] === 'interested_party'
    },
    getContactSortName(item) {
      if (item?.name && String(item.name).trim()) {
        return String(item.name).trim()
      }
      const lastName = item?.lastName || ''
      const firstName = item?.firstName || ''
      return `${lastName} ${firstName}`.trim()
    },
    mapDuplicatedContactForBuffer(contact, selectedOwner) {
      const types = this.normalizeContactTypes(contact.types)
      const name = this.getContactSortName(contact)
      return {
        ...contact,
        objectID: contact.id,
        name: name || contact.name,
        types,
        isOnlyInterestedParty: this.computeIsOnlyInterestedParty(contact.types),
        owner: { name: selectedOwner?.ownerDisplayName || selectedOwner?.name || '-' },
        isEmployee: false,
        canBeSetInactive: true
      }
    },
    getDuplicatedContactsBufferStorageKey() {
      return `contact-duplicated-buffer-${this.$route.name}-${this.accountId}`
    },
    persistDuplicatedContactsBuffer() {
      const key = this.getDuplicatedContactsBufferStorageKey()
      if (!this.duplicatedContactsBuffer || this.duplicatedContactsBuffer.length === 0) {
        sessionStorage.removeItem(key)
        return
      }
      const payload = {
        savedAt: Date.now(),
        contacts: this.duplicatedContactsBuffer
      }
      sessionStorage.setItem(key, JSON.stringify(payload))
    },
    restoreDuplicatedContactsBuffer() {
      const key = this.getDuplicatedContactsBufferStorageKey()
      const raw = sessionStorage.getItem(key)
      if (!raw) {
        return
      }
      try {
        const parsed = JSON.parse(raw)
        const isExpired = !parsed?.savedAt || Date.now() - parsed.savedAt > this.duplicatedContactsBufferStorageTtlMs
        const contacts = Array.isArray(parsed?.contacts) ? parsed.contacts : []
        if (isExpired || contacts.length === 0) {
          sessionStorage.removeItem(key)
          return
        }
        this.duplicatedContactsBuffer = contacts.map(contact => {
          const types = this.normalizeContactTypes(contact.types)
          const name = this.getContactSortName(contact)
          return {
            ...contact,
            types,
            name: name || contact.name,
            isOnlyInterestedParty: contact.isOnlyInterestedParty ?? this.computeIsOnlyInterestedParty(contact.types)
          }
        })
      } catch (e) {
        sessionStorage.removeItem(key)
      }
    },
    openAssembleListModal() {
      this.$bvModal.show('assembleListModal')
    },
    getAccountDisplayName(account) {
      if (account.company) {
        return account.company.name
      }
      if (account.individual) {
        return `${account.individual.lastName} ${account.individual.firstName}`
      }
      return 'n/a'
    },
    async loadDuplicateOwnerAccounts() {
      try {
        const response = await ShareApi.listByTargetAccountId(this.accountId)
        const listAccountIds = []
        response.shares.forEach(share => {
          if (!listAccountIds.includes(share.sourceAccountId)) {
            listAccountIds.push(share.sourceAccountId)
          }
        })
        if (!listAccountIds.includes(this.accountId)) {
          listAccountIds.push(this.accountId)
        }
        const userResponse = await UserApi.listAccountsByIds(listAccountIds)
        const accounts = userResponse.accounts.map(account => {
          const accountDisplayName = this.getAccountDisplayName(account)
          const isOwnAccount = String(account.id) === String(this.accountId)
          return {
            id: account.id,
            ownerDisplayName: accountDisplayName,
            name: isOwnAccount ? this.$t('message.contact.forYourself') : accountDisplayName
          }
        })
        const ownAccount = accounts.find(account => String(account.id) === String(this.accountId))
        const otherAccounts = accounts
          .filter(account => String(account.id) !== String(this.accountId))
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        this.duplicateOwnerAccounts = ownAccount ? [ownAccount, ...otherAccounts] : otherAccounts
      } catch (e) {
        console.log(e)
        this.duplicateOwnerAccounts = []
        Vue.toasted.show(this.$t('message.loadingErrors.owners'), { type: 'error' })
      }
    },
    async openDuplicateContactsModal() {
      this.selectedDuplicateOwnerId = null
      await this.loadDuplicateOwnerAccounts()
      this.$bvModal.show('duplicate-contact-modal')
    },
    closeDuplicateContactsModal() {
      this.selectedDuplicateOwnerId = null
      this.$bvModal.hide('duplicate-contact-modal')
    },
    loadFields(setting) {
      const result = this.getFields(this.dataFields, setting, true, false)
      this.fields = result?.fields
      if (this.mobileView) {
        this.selectedFields = this.selectedFields.filter(x => ['checkbox', 'contactName', 'address', 'communication'].includes(x.key))
        this.fields = this.fields.filter(x => ['checkbox', 'contactName', 'address', 'communication'].includes(x.key))
      } else {
        this.selectedFields = result?.selectedFields
      }
      this.$refs.contactOverviewTable?.refresh()
    },
    saveFields(data) {
      const columns = data.filter(x => x.selected === true).map(y => y.key)
      const src = 'contactOverview'
      SearchApi.saveSettings(this.accountId, columns, src).then(response => {
        const setting = response.tableSettings
        if (setting && setting.columns.length > 0) {
          this.loadFields(setting)
        }
        this.$bvModal.hide('assembleListModal')
      })
    },
    createTicket() {
      const defaultAssignee = this.selectedEntries.every((val, i, arr) => val.owner.id === arr[0].owner.id) ? this.selectedEntries[0].owner.id : ''
      if (this.isOwnerModule) {
        this.$router.push({ name: 'OwnerTicketCreation', query: { fromBulk: true, ids: this.selectedEntriesIds, defaultAssignee: defaultAssignee } })
      } else if (this.isAdminModule) {
        this.$router.push({ name: 'AdminTicketCreation', query: { fromBulk: true, ids: this.selectedEntriesIds, defaultAssignee: defaultAssignee } })
      } else if (this.isAssetModule) {
        this.$router.push({ name: 'AssetTicketCreation', query: { fromBulk: true, ids: this.selectedEntriesIds, defaultAssignee: defaultAssignee } })
      } else if (this.isAccountingModule) {
        this.$router.push({ name: 'AccountingTicketCreation', query: { fromBulk: true, ids: this.selectedEntriesIds, defaultAssignee: defaultAssignee } })
      }
    },
    filterRoleOption(items) {
      if (this.filterShowInterestedParties) {
        return items
      }
      return items.filter(x => x.value !== 'INTERESTED_PARTY')
    },
    getFilterIndustriesOptions(items) {
      return items.filter(x => x.value !== 'INDUSTRY_UNDEFINED_VALUE')
    },
    getFilterTagsOptions(items) {
      return items
    },
    clickOnClear() {
      this.initFilter()
      this.showFilters = false
    },
    initFilter() {
      this.filterOwner = []
      this.filterRole = []
      this.filterIndustries = []
      this.filterTags = []
      this.filterStatus = 'all'
      this.filterShowInterestedParties = false
      this.filterPreferredCommunicationChannel = null
      this.ownerFacetQuery = ''
      this.searchQuery = ''
      this.currentPage = 1
      this.$refs.searchBox?.setValue('')
      this.clearCheckboxFilterShowInterestedParties()
      this.clearCheckbox()
    },
    clearCheckbox() {
      this.selectedEntries = []
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
      this.handleCheckboxesInsideTable(false)
    },
    clearCheckboxFilterShowInterestedParties() {
      const showInterestedPartyCheckbox = this.$refs.isOnlyInterestedParty
      if (showInterestedPartyCheckbox?.$el) {
        const input = showInterestedPartyCheckbox.$el.querySelector('input[type="checkbox"]')
        if (input) {
          input.checked = false
          const event = new Event('change', { bubbles: true })
          input.dispatchEvent(event)
        }
      }
    },
    filteredHits(array) {
      const hits = array.filter(x => x.isEmployee !== true)
      if (this.duplicatedContactsBuffer.length === 0) {
        return hits
      }
      const visibleIds = new Set(hits.map(x => x.objectID || x.id))
      let remainingBufferedContacts = this.duplicatedContactsBuffer.filter(contact => !visibleIds.has(contact.objectID))
      if (!this.filterShowInterestedParties) {
        remainingBufferedContacts = remainingBufferedContacts.filter(contact => !contact.isOnlyInterestedParty)
      }
      if (remainingBufferedContacts.length !== this.duplicatedContactsBuffer.length) {
        this.duplicatedContactsBuffer = remainingBufferedContacts
        this.persistDuplicatedContactsBuffer()
      }
      const merged = remainingBufferedContacts.concat(hits)
      const nameKey = item => this.getContactSortName(item).toLowerCase()
      const idKey = item => String(item.objectID || item.id || '')
      merged.sort((a, b) => {
        const byName = nameKey(a).localeCompare(nameKey(b))
        if (byName !== 0) {
          return byName
        }
        return idKey(a).localeCompare(idKey(b))
      })
      return merged
    },
    onRowClicked(item, index, event) {
      if (this.linkContact) {
        this.selectedLinkContact = item.objectID
        this.$refs.confirmationModalLinkContact.show()
      } else if (this.linkMail) {
        this.selectedRow = item
        this.$refs['link-mail-to-contact'].show()
      } else {
        const target = event?.target || event?.srcElement
        const isCheckboxClick = Boolean(target?.closest?.('.checkbox-cell') ||
          target?.closest?.('.custom-checkbox') ||
          target?.closest?.('.custom-control-label') ||
          target?.closest?.('.custom-control-input'))
        if (isCheckboxClick) {
          return
        }
        this.showDetail(item)
      }
    },
    serialLetter() {
      const uniqKey = uuidv4()
      localStorage.setItem(uniqKey, this.selectedEntriesIds)
      this.$nextTick(() => {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isMarketingModule) {
          this.$router.push({
            name: 'MarketingSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              previousRoute: this.$route.fullPath
            }
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingSerialLetterProcessView',
            query: {
              source: 'contact',
              uuid: uniqKey,
              previousRoute: this.$route.fullPath
            }
          })
        }
      })
    },
    showActivity() {
      this.keyActivity = Math.random().toString(36).substr(2, 9)
      this.$nextTick(() => {
        this.$refs['activity-creation'].show()
      })
    },
    linkContacts() {
      this.disabledLinkBtn = true
      if (this.selectedLinkContact === this.linkContact) {
        Vue.toasted.show(this.$t('message.savingErrors.notSamePersonLinked'), { type: 'error' })
        this.$refs.confirmationModalLinkContact.hide()
        this.disabledLinkBtn = false
      } else {
        if (this.linkContactType === 'person') {
          ContactApi.linkPersons(this.selectedLinkContact, this.linkContact)
            .then(() => {
              this.$refs.confirmationModalLinkContact.hide()
              this.selectedLinkContact = []
              if (this.$route.name === 'MarketingContactOverview') {
                this.$router.push({ name: 'MarketingContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AdminContactOverview') {
                this.$router.push({ name: 'AdminContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AssetContactOverview') {
                this.$router.push({ name: 'AssetContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AccountingContactOverview') {
                this.$router.push({ name: 'AccountingContactDetailsView', params: { id: this.linkContact } })
              } else {
                this.$router.push({ name: 'OwnerContactDetailsView', params: { id: this.linkContact } })
              }
              this.disabledLinkBtn = false
              Vue.toasted.show(this.$t('message.successMessages.contactLinked'), { type: 'success' })
            })
            .catch(e => {
              this.disabledLinkBtn = false
              console.log(e)
              Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
            })
        } else {
          ContactApi.linkCompanies(this.selectedLinkContact, this.linkContact)
            .then(() => {
              this.$refs.confirmationModalLinkContact.hide()
              this.selectedLinkContact = []
              if (this.$route.name === 'MarketingContactOverview') {
                this.$router.push({ name: 'MarketingContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AdminContactOverview') {
                this.$router.push({ name: 'AdminContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AssetContactOverview') {
                this.$router.push({ name: 'AssetContactDetailsView', params: { id: this.linkContact } })
              } else if (this.$route.name === 'AccountingContactOverview') {
                this.$router.push({ name: 'AccountingContactDetailsView', params: { id: this.linkContact } })
              } else {
                this.$router.push({ name: 'OwnerContactDetailsView', params: { id: this.linkContact } })
              }
              this.disabledLinkBtn = false
              Vue.toasted.show(this.$t('message.successMessages.contactLinked'), { type: 'success' })
            })
            .catch(e => {
              this.disabledLinkBtn = false
              console.log(e)
              Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
            })
        }
      }
    },
    contactId(contact) {
      return contact?.objectID || contact?.id
    },
    isContactSelected(item) {
      const id = this.contactId(item)
      return Boolean(id) && this.selectedEntryIdsSet.has(id)
    },
    contactSelected(contact, checked) {
      const contactId = this.contactId(contact)
      if (checked) {
        if (contactId && !this.selectedEntryIdsSet.has(contactId)) {
          this.selectedEntries.push(contact)
        }
        const visible = this.filteredHits(this.pageHits)
        if (visible.length && visible.every(item => this.isContactSelected(item))) {
          this.selectAllVisibleEntries = true
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter(entry => this.contactId(entry) !== contactId)
        this.selectAllEntries = false
        this.selectAllVisibleEntries = false
      }
    },
    openConfirmationModal() {
      this.$refs.confirmationModal.show()
    },
    /** Waits for the search read model to catch up, then refreshes the list. */
    async reloadResults(entityIds = []) {
      await awaitSearchIndexUpdate(entityIds)
      await this.$refs.contactSearch?.reload()
      this.refreshDone()
    },
    deactivateContact() {
      this.disabledDeleteBtn = true
      const ids = this.selectedEntriesIds
      executeBulkInChunks(ids, 300, (chunk) => ContactApi.deactivateContact(chunk))
        .then(() => {
          this.action = 'contactInActivated'
          this.reloadResults(ids)
        })
        .catch(e => {
          this.disabledDeleteBtn = false
          console.log(e)
          if (e.response?.status === 400 && e.response?.data?.code === 9) {
            Vue.toasted.show(this.$t('message.deleteErrors.contactisActiveTenancy'), { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
          }
        })
    },
    activateContact() {
      this.disabledDeleteBtn = true
      const ids = this.selectedEntriesIds
      executeBulkInChunks(ids, 300, (chunk) => ContactApi.activateContact(chunk))
        .then(() => {
          this.action = 'contactActivated'
          this.reloadResults(ids)
        })
        .catch(e => {
          this.disabledDeleteBtn = false
          console.log(e)
          Vue.toasted.show(this.$t('message.deleteErrors.contact'), { type: 'error' })
        })
    },
    customLabel(item) {
      return this.$t('message.contact.types.' + item.label.toUpperCase()) + ' (' + item.count + ')'
    },
    customLabelTags(item) {
      return item.label + ' (' + item.count + ')'
    },
    customLabelIndustries(item) {
      return this.$t('message.contact.industries.' + item.label) + ' (' + item.count + ')'
    },
    customOwnerLabel(item) {
      return item.label + ' (' + item.count + ')'
    },
    getListRole(contact) {
      const types = this.normalizeContactTypes(contact.types)
      if (types.length === 0 || (types.length === 1 && types[0] === 'none')) {
        return '-'
      }
      let contactType = types
      if (contactType.includes('tenant') && !this.filterShowInterestedParties) {
        contactType = contactType.filter(x => x !== 'interested_party')
      }
      const array = []
      contactType.forEach(role => {
        if (role !== 'none') {
          array.push(this.$t('message.contact.types.' + role.toUpperCase()))
        }
      })
      return array.length > 0 ? array.join(', ') : '-'
    },
    contactChanged(data) {
      if (this.linkContact) {
        this.selectedLinkContact = data.contact.id
        this.linkContacts()
      } else if (this.linkMail) {
        this.selectedRow = data.contact
        this.linkMailToContact()
      } else if (data.addingAnother === true || data.takeOverAddress === true) {
        this.action = 'contactAdded'
        this.reloadResults(data.contact ? [data.contact.id] : [])
      } else if (data.contact) {
        this.redirectToDetailPage(data.contact.id)
      }
    },
    editAddress() {
      this.$bvModal.show('add-address-modal')
    },
    addressAdded() {
      this.action = 'addressAdded'
      this.reloadResults(this.selectedEntriesIds)
    },
    duplicateContactsToOwnerbase() {
      if (!this.selectedDuplicateOwnerId || this.selectedEntriesIds.length === 0) {
        return
      }
      this.duplicatingContacts = true
      const sourceIds = this.selectedEntriesIds
      executeBulkInChunks(sourceIds, 200, (chunk) =>
        ContactApi.duplicateContacts(chunk, this.selectedDuplicateOwnerId)
      )
        .then(responses => {
          const selectedOwner = this.duplicateOwnerAccounts.find(account => account.id === this.selectedDuplicateOwnerId)
          const duplicatedCompanies = responses.flatMap(response => response.companies || []).map(company =>
            this.mapDuplicatedContactForBuffer(company, selectedOwner)
          )
          const duplicatedPersons = responses.flatMap(response => response.persons || []).map(person => ({
            ...this.mapDuplicatedContactForBuffer(person, selectedOwner),
            contactPersons: []
          }))
          this.duplicatedContactsBuffer = duplicatedCompanies.concat(duplicatedPersons)
          this.persistDuplicatedContactsBuffer()
          this.$bvModal.hide('duplicate-contact-modal')
          this.selectedDuplicateOwnerId = null
          this.clearCheckbox()
          this.action = 'contactDuplicated'
          this.reloadResults(this.duplicatedContactsBuffer.map(x => x.objectID))
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.contactDuplicate'), { type: 'error' })
        })
        .finally(() => {
          this.duplicatingContacts = false
        })
    },
    handleCheckboxesInsideTable(value) {
      if (value) {
        this.selectedEntries = [...this.filteredHits(this.pageHits)]
      } else {
        this.selectedEntries = []
      }
      this.selectAllVisibleEntries = value
      if (!value) {
        this.selectAllEntries = false
      }
    },
    showDetail(item) {
      if (this.$route.name === 'MarketingContactOverview') {
        this.$router.push({ name: 'MarketingContactDetailsView', params: { id: item.objectID || item.id } })
      } else if (this.$route.name === 'AdminContactOverview') {
        this.$router.push({ name: 'AdminContactDetailsView', params: { id: item.objectID || item.id } })
      } else if (this.$route.name === 'AssetContactOverview') {
        this.$router.push({ name: 'AssetContactDetailsView', params: { id: item.objectID || item.id } })
      } else if (this.$route.name === 'AccountingContactOverview') {
        this.$router.push({ name: 'AccountingContactDetailsView', params: { id: item.objectID || item.id } })
      } else {
        this.$router.push({ name: 'OwnerContactDetailsView', params: { id: item.objectID || item.id } })
      }
    },
    onMiddlelicked(item) {
      let route = null
      if (this.$route.name === 'MarketingContactOverview') {
        route = this.$router.resolve({ name: 'MarketingContactDetailsView', params: { id: item.objectID } })
      } else if (this.$route.name === 'AdminContactOverview') {
        route = this.$router.resolve({ name: 'AdminContactDetailsView', params: { id: item.objectID } })
      } else if (this.$route.name === 'AssetContactOverview') {
        route = this.$router.resolve({ name: 'AssetContactDetailsView', params: { id: item.objectID } })
      } else if (this.$route.name === 'AccountingContactOverview') {
        route = this.$router.resolve({ name: 'AccountingContactDetailsView', params: { id: item.objectID } })
      } else {
        route = this.$router.resolve({ name: 'OwnerContactDetailsView', params: { id: item.objectID } })
      }
      window.open(route.href)
    },
    async serialEmail() {
      let contacts = []
      const selectedContactPersonsIds = []
      const listEntries = this.selectAllEntries ? this.allEntries : this.selectedEntries
      this.setSelectedEntries(listEntries)

      const prepToken = this.callSendMailTypeModal()
      eventBus.$emit('set-mail-type-loading', true)

      try {
        if (this.selectedEntriesIds.length === 0) {
          return
        }
        await ContactApi.contactResolve(this.selectedEntriesIds)
          .then(response => {
            if (response.persons.length > 0) {
              contacts = response.persons
            }
            if (response.companies.length > 0) {
              const companies = response.companies
              const companiesWantEmail = companies.filter(x => x.includeInSerialEmails)
              contacts = contacts.concat(companiesWantEmail)
              if (companies.length > 0) {
                companies.forEach(company => {
                  const companyContacts = company.contactPersons || []
                  companyContacts.forEach(contact => {
                    if (contact.includeInSerialEmails) {
                      contacts.push(contact)
                      selectedContactPersonsIds.push(contact.id)
                    }
                  })
                })
              }
            }
          })

        if (!isMailTypePrepCurrent(prepToken)) {
          return
        }

        contacts = contacts.sort((a, b) => {
          return this.selectedEntriesIds.indexOf(a.id) - this.selectedEntriesIds.indexOf(b.id)
        })

        const contactsWithEmail = contacts
          .filter(x => this.hasSendableEmail(x) && (this.selectedEntriesIds.includes(x.id) || selectedContactPersonsIds.includes(x.id)))
          .flatMap(x => this.getSendableEmailEntries(x))
        const contactsWithoutEmail = contacts.filter(x => x && !this.hasSendableEmail(x) && this.selectedEntriesIds.includes(x.id))
        this.setSendMailContactsWithoutEmail(contactsWithoutEmail)
        this.setSendMailContacts(contactsWithEmail)
        this.setSendMailPreselectedContact(contacts)
        this.setSendMailReferenceIds([])
        this.setSendMailEmailToReply(null)
        // this.setUseBcc(true)
        this.setSendMailSecondaryTitle(true)
      } finally {
        if (isMailTypePrepCurrent(prepToken)) {
          eventBus.$emit('set-mail-type-loading', false)
        }
      }
    },
    sendEmailModal(contact) {
      contact.id = contact.objectID
      this.setSendMailContacts([contact])
      this.setSendMailPreselectedContact(contact)
      this.setSendMailReferenceIds([])
      this.setSendMailEmailToReply(null)
      // this.setUseBcc(true)
      this.$nextTick(function () {
        this.callShowSendMailModal()
      })
    },
    linkMailToContact() {
      const existingReferenceIds = (this.email && this.email.referenceIds) || []
      MessageApi.setReferenceIds([this.linkMail], [this.selectedRow.objectID || this.selectedRow.id].concat(existingReferenceIds.filter(x => !x.includes('contact_'))))
        .then(() => {
          Vue.toasted.show(this.$t('message.successMessages.messageLinked'), { type: 'success' })
          if (this.email !== '') {
            this.showDetail(this.selectedRow)
          } else {
            if (this.isOwnerModule) {
              this.$router.push({ name: 'OwnerMessagesView' })
            } else if (this.isMarketingModule) {
              this.$router.push({ name: 'MarketingMessagesView' })
            } else if (this.isAdminModule) {
              this.$router.push({ name: 'AdminMessagesView' })
            } else if (this.isAssetModule) {
              this.$router.push({ name: 'AssetMessagesView' })
            } else if (this.isAccountingModule) {
              this.$router.push({ name: 'AccountingMessagesView' })
            }
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.messageLinked'), { type: 'error' })
        })
    },
    refreshDone() {
      if (this.$refs.confirmationModal) {
        this.$refs.confirmationModal.hide()
      }
      this.clearCheckbox()
      this.disabledDeleteBtn = false
      if (this.action === 'contactAdded') {
        // Do nothing
      } else if (this.action === 'addressAdded') {
        Vue.toasted.show(this.$t('message.successMessages.addressChanged'), { type: 'success' })
      } else if (this.action === 'contactDuplicated') {
        Vue.toasted.show(this.$t('message.successMessages.contactDuplicated'), { type: 'success' })
      } else if (this.action === 'contactActivated') {
        Vue.toasted.show(this.$t('message.successMessages.contactActivated'), { type: 'success' })
      } else if (this.action === 'contactInActivated') {
        Vue.toasted.show(this.$t('message.successMessages.contactInactivated'), { type: 'success' })
      }
      this.action = ''
    },
    getUrlWebsite(item) {
      if (item.indexOf('//') > -1) {
        return item
      } else {
        return '//' + item
      }
    }
  }
}
</script>

<style lang="scss" scoped>

  .role-list {
    list-style-type: none;
  }

  .title-name {
    line-height: 2;
  }

  .label-padding-top-005rem :deep(label) {
    padding-top: 0.05rem;
  }
  @media (max-width: 767px) {
    :deep(.b-table-stacked-md > tbody > tr > [data-label]::before) {
      text-align: left !important;
    }
    :deep(.table-responsive-true) table.table > tbody > tr:nth-child(even) td, :deep(.table-responsive-true) table.table > tbody > tr:nth-child(odd) td {
      border-bottom: 0px;
    }
    :deep(.table-responsive-true) table.table > tbody > tr td:nth-child(1) {
      border-bottom: 1px solid rgba(0, 68, 95, 0.15);
      border-top: 1px solid rgba(0, 68, 95, 0.15);
    }
  }
</style>
